import { translatePaymentStatus, translatePaymentType } from "@qgiv/core-js";
import {
    getDonationSubtotal,
    getRegistrationSubtotal,
} from "../p2pReceiptHelpers";

/**
 *
 * @param {object} registration Registration data from Redux Store.
 * @returns {object} Object with keys specified by documentation.
 */
export const getContact = (registration = {}) => {
    const { payment = {}, registrations = [] } = registration;
    // All of the contact data should come from the primary registrant
    const primaryRegistrant = registrations[0];
    const { Email = "", First_Name = "", Last_Name = "" } = primaryRegistrant;
    const { Privacy_Display_Name = true } = payment;
    let contact = {};

    // Assign values to keys specified by documentation. The optedIn key is
    // included for consistency even though that field is not present in the
    // P2P registration form. The data that is added to the anonymous key is
    // extracted from the Privacy_Display_Name field as we do not have an exact
    // match for the Anonymity field. However this field does ask the primary
    // registrant "Show my name as a supporter on public event pages" so we
    // have inferred that if they do not want us to show their name they want
    // to appear anonymous.
    const email = Email;
    const firstName = First_Name;
    const givenAnonymously = !Privacy_Display_Name;
    const lastName = Last_Name;
    const optedIn = false;

    contact = {
        email,
        firstName,
        givenAnonymously,
        lastName,
        optedIn,
    };

    return contact;
};

/**
 *
 * @param {object} receipt Receipt data from Redux Store.
 * @returns {object} Object with keys specified by documentation.
 */
export const getEcommerce = (receipt = {}) => {
    const hasReceipt = Object.keys(receipt).length > 0;
    let ecommerce = {};

    if (!hasReceipt) {
        return ecommerce;
    }

    // Prefix transaction data with "raw" to prevent a naming conflict
    const { transaction: rawTransaction = {} } = receipt;
    const { merchantAccount = {} } = rawTransaction;
    const { currency = "USD" } = merchantAccount;
    // Only has a single item at this point
    const items = [
        {
            item_name: rawTransaction.formName,
            price: Number(rawTransaction.amount),
        },
    ];
    const transaction_id = String(rawTransaction.id);
    const value = rawTransaction.amount;

    ecommerce = {
        currency,
        items,
        transaction_id,
        value,
    };

    return ecommerce;
};

/**
 *
 * @param {object} formSettings Form settings data from Redux Store.
 * @returns {object} Object with keys specified by documentation.
 */
export const getForm = (formSettings) => {
    // Extract data from state
    const { id: idAsNumber = 0, name = "" } = formSettings;
    const id = String(idAsNumber);

    // Assign values to keys specified by documentation
    const form = {
        id,
        name,
    };

    return form;
};

/**
 *
 * @returns {object} Object with keys specified by documentation.
 * @param {object} receipt Receipt data from Redux Store.
 */
export const getTransaction = (receipt = {}) => {
    const hasReceipt = Object.keys(receipt).length > 0;
    let transaction = {};

    if (!hasReceipt) {
        return transaction;
    }

    // Prefix transaction data with "raw" to prevent a naming conflict
    const { feeCoverage = "", transaction: rawTransaction = {} } = receipt;
    const { id: idAsNumber = 0, merchantAccount = {} } = rawTransaction;

    // Calculate subtotals
    const donationsSubtotal = getDonationSubtotal(receipt);
    const eventRegistrationsSubtotal = getRegistrationSubtotal(receipt);

    // Hard coded for now as this functionality is not currently present in
    // P2P registration but is something that we are looking to potentially
    // support in the future.
    const associatedInfo = "";
    // Format and associate the data with variables that match keys
    const { currency = "USD" } = merchantAccount;
    const date = rawTransaction.tm_stamp;
    const demo = rawTransaction.demo === "y";
    const donations = Number(donationsSubtotal);
    const eventRegistrations = Number(eventRegistrationsSubtotal);
    const id = String(idAsNumber);
    // If the registrant did not add giftAssist, feeCoverage is an empty
    // string, but it is a number if the registrant did add gift assist.
    const giftAssist = Number(feeCoverage);
    const paymentMethod = translatePaymentType(
        rawTransaction.ccType,
        rawTransaction.channel,
    );
    const status = translatePaymentStatus(rawTransaction.status);
    const timestamp = String(rawTransaction.tm_stamp_unix);
    const total = Number(rawTransaction.amount);

    transaction = {
        associatedInfo,
        currency,
        date,
        demo,
        donations,
        eventRegistrations,
        giftAssist,
        id,
        paymentMethod,
        status,
        timestamp,
        total,
    };

    return transaction;
};

/**
 *
 * @param {object} options Object that contains all of the arguments.
 * @param {object} options.formSettings Form settings data from Redux Store.
 * @param {object} options.receipt Receipt data from Redux Store.
 * @param {object} options.registration Registration data from Redux Store.
 * @returns {object} Object with keys specified by documentation.
 */
export const getDataLayerOptions = ({
    formSettings = {},
    receipt = {},
    registration = {},
}) => {
    const defaultPayload = {
        contact: {},
        ecommerce: {},
        form: {},
        transaction: {},
        utm: {},
    };
    let payload = {};

    const contact = getContact(registration);
    const ecommerce = getEcommerce(receipt);
    const form = getForm(formSettings);
    const transaction = getTransaction(receipt);

    payload = {
        ...defaultPayload,
        contact,
        ecommerce,
        form,
        transaction,
    };

    return payload;
};

export default getDataLayerOptions;
